import { Observable, Subscribable, Subscription } from 'knockout';

type BindingsConfig<T> = { [P in keyof T]?: T[P] extends Subscribable<infer K> ? Subscribable<K> : never }

export class WCCEntityAdapterBuilder {
    constructor() { }

    /**
     * Creates multiple bindings from config
     * shortcut for multiple bindFields calls
     * @param config
     */
    createBindings<T>(adapter: T, config: BindingsConfig<T>) {
        _(config).each((realField, adaptedFieldName: any) =>
            this.bindFields((adapter as any)[adaptedFieldName], realField as any));
    }

    /**
     * Binds two fields 
     * @param adaptedField field from entity we want to get
     * @param realField field from entity we write adapter for
     */
    bindFields<T>(adaptedField: Observable<T>, realField: Observable<T>) {
        if (ko.isWritableObservable(realField))
            adaptedField.subscribe(this.getUpdater(realField));

        this.addLazyBinding(realField, adaptedField);

        return this;
    }

    /**
     * Updates target from source only when target is awake
     * Allows to subscribe to source notifications only when we really need it
     * @param source observable we read from
     * @param target observable we write to
     */
    private addLazyBinding<T>(source: Observable<T>, target: Observable<T>) {
        const updater = this.getUpdater(target);
        let subscription: Subscription | undefined;

        function init() {
            subscription = source.subscribe(updater);
            updater(source());
        }

        target.subscribe(init, undefined, 'awake');

        target.subscribe(() => {
            subscription?.dispose();
            subscription = undefined;
        }, undefined, 'asleep');

        if (target.getSubscriptionsCount() > 0)
            init();
    }

    /**
     * creates function which will update observable 
     * and can be used as subscribe callback in compatible observable
     * performs update only if new value is different from current one
     * @param observable
     */
    private getUpdater<T>(observable: Observable<T>) {
        return (value: T) => {
            const currentValue = observable();

            if (currentValue !== value)
                observable(value);
        }
    }
}

const wccEntityAdapterBuilder = new WCCEntityAdapterBuilder;

export default wccEntityAdapterBuilder;